<template>
  <div class="page-container">
    <PageHeader title="消息查询">
      <template #actions>
        <ArchiveAppSelect v-model="currentApp" @change="handleAppChange" />
      </template>
    </PageHeader>

    <div v-if="!currentApp" class="qw-message-list__placeholder">
      <TablePanel>
        <EmptyState
          icon="el-icon-connection"
          title="请先选择接入方"
          description="会话存档接口需以接入方身份调用（携带 appId/appSecret），请从右上角选择"
        />
      </TablePanel>
    </div>

    <template v-else>
      <SearchPanel>
        <el-select
          v-model="query.roomId"
          size="small"
          filterable
          clearable
          allow-create
          default-first-option
          placeholder="群 chatId（可下拉选/手输）"
          style="width: 220px"
        >
          <el-option v-for="g in groupOptions" :key="g.chatId" :label="g.name || g.chatId" :value="g.chatId" />
        </el-select>
        <el-input v-model.trim="query.fromUserid" size="small" placeholder="发送人 userid" clearable @keyup.enter.native="handleSearch" />
        <el-select v-model="query.fromType" size="small" clearable placeholder="发送人类型" style="width: 150px">
          <el-option label="企业成员" :value="1" />
          <el-option label="外部联系人" :value="2" />
        </el-select>
        <el-select v-model="query.msgtype" size="small" clearable filterable placeholder="消息类型" style="width: 130px">
          <el-option v-for="t in msgtypeOptions" :key="t" :label="t" :value="t" />
        </el-select>
        <el-input v-model.trim="query.content" size="small" placeholder="内容关键字" clearable @keyup.enter.native="handleSearch" />
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          size="small"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="timestamp"
          :default-time="['00:00:00', '23:59:59']"
          style="width: 360px"
        />
        <template #actions>
          <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" @click="handleSearch">查 询</el-button>
          <el-button size="small" icon="el-icon-refresh-left" @click="handleReset">重 置</el-button>
        </template>
      </SearchPanel>

      <TablePanel>
        <el-table v-loading="loading" :data="messageList" stripe>
          <el-table-column label="消息时间" min-width="160">
            <template slot-scope="{ row }">{{ formatDateTime(row.msgtime) }}</template>
          </el-table-column>
          <el-table-column label="发送人" min-width="130">
            <template slot-scope="{ row }">{{ row.fromUserid || '-' }}</template>
          </el-table-column>
          <el-table-column label="发送人类型" width="105" align="center">
            <template slot-scope="{ row }">
              <span class="qw-tag" :class="row.fromType === 1 ? 'qw-tag--primary' : 'qw-tag--warning'">
                {{ row.fromType === 1 ? '企业成员' : (row.fromType === 2 ? '外部联系人' : '-') }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="类型" width="95" align="center">
            <template slot-scope="{ row }">
              <!-- 复用 msgtypeTagType 的语义映射换 qw-tag 类名（text 无语义时回落 primary） -->
              <span class="qw-tag" :class="'qw-tag--' + (msgtypeTagType(row.msgtype) || 'primary')">{{ row.msgtype }}</span>
            </template>
          </el-table-column>
          <el-table-column label="所属群" min-width="150" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span v-if="row.roomid" class="qw-copy-text">{{ groupName(row.roomid) }}</span>
              <span v-else class="qw-tag qw-tag--info">单聊</span>
            </template>
          </el-table-column>
          <el-table-column label="消息内容" min-width="240">
            <template slot-scope="{ row }">
              <el-tooltip v-if="row.content" :content="row.content" placement="top" :open-delay="300">
                <span class="qw-text-ellipsis" style="display: inline-block; max-width: 220px">{{ row.content }}</span>
              </el-tooltip>
              <span v-else class="qw-message-list__no-content">（无文本内容）</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" align="center" class-name="aj-table-cell">
            <template slot-scope="{ row }">
              <!-- 有 sdkfileid 的消息（图片/语音/视频/文件等）可下载媒体文件 -->
              <el-link
                v-if="row.sdkfileid"
                class="aj-table-operation-link aj-table-operation-link--info"
                icon="el-icon-download"
                :underline="false"
                @click="downloadMedia(row)"
              >下载</el-link>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page.sync="query.currentPage"
          :page-size.sync="query.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          @current-change="fetchMessageList"
          @size-change="handleSearch"
        />
      </TablePanel>
    </template>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import EmptyState from '@/components/EmptyState.vue'
import ArchiveAppSelect from '@/components/ArchiveAppSelect.vue'
import { getMessagePage, getGroupList, buildMediaDownloadUrl } from '@/api/archive'
import { formatDateTime } from '@/utils/format'

// 消息查询：多条件分页查询会话存档消息（/qw-archive/message/page）
export default {
    name: 'MessageList',
    components: { PageHeader, SearchPanel, TablePanel, EmptyState, ArchiveAppSelect },
    data () {
        return {
            currentApp: null,
            loading: false,
            messageList: [],
            total: 0,
            // 企微会话存档常见消息类型（后端按 msgtype 精确匹配）
            msgtypeOptions: ['text', 'image', 'revoke', 'voice', 'video', 'file', 'link', 'weapp', 'chatrecord', 'emotion', 'location', 'markdown', 'gray'],
            groupOptions: [],
            timeRange: null,
            query: {
                roomId: '',
                fromUserid: '',
                fromType: null,
                msgtype: '',
                content: '',
                startTime: null,
                endTime: null,
                currentPage: 1,
                pageSize: 20
            }
        }
    },
    methods: {
        formatDateTime,
        // 群 chatId → 群名（tooltip 里展示 chatId 便于核对）
        groupName (chatId) {
            const group = this.groupOptions.find(g => g.chatId === chatId)
            return group ? (group.name || chatId) : chatId
        },
        msgtypeTagType (msgtype) {
            const map = {
                text: '',
                image: 'success',
                video: 'danger',
                voice: 'warning',
                file: 'info',
                emotion: 'warning',
                revoke: 'info'
            }
            return map[msgtype] || 'info'
        },
        handleAppChange () {
            this.query.currentPage = 1
            if (this.currentApp) {
                this.fetchGroupOptions()
                this.fetchMessageList()
            } else {
                this.messageList = []
                this.total = 0
                this.groupOptions = []
            }
        },
        // 拉取群选项（前 100 个）供「所属群」下拉筛选与表格群名展示
        async fetchGroupOptions () {
            try {
                const res = await getGroupList({ currentPage: 1, pageSize: 100 }, this.currentApp)
                this.groupOptions = res.data || []
            } catch (error) {
                // 群选项加载失败不阻塞消息查询
            }
        },
        async fetchMessageList () {
            if (!this.currentApp) return
            this.loading = true
            // datetimerange 选择器 value-format=timestamp，直接得到毫秒时间戳
            this.query.startTime = this.timeRange && this.timeRange[0] ? this.timeRange[0] : null
            this.query.endTime = this.timeRange && this.timeRange[1] ? this.timeRange[1] : null
            try {
                // 空字符串/null 统一交由 qs 序列化剔除，后端按非必填处理
                const res = await getMessagePage(this.query, this.currentApp)
                this.messageList = res.data || []
                this.total = Number(res.total || 0)
            } catch (error) {
                // 错误提示已由 request 拦截器统一弹出
            } finally {
                this.loading = false
            }
        },
        handleSearch () {
            this.query.currentPage = 1
            this.fetchMessageList()
        },
        handleReset () {
            this.timeRange = null
            this.query = {
                roomId: '',
                fromUserid: '',
                fromType: null,
                msgtype: '',
                content: '',
                startTime: null,
                endTime: null,
                currentPage: 1,
                pageSize: this.query.pageSize
            }
            this.fetchMessageList()
        },
        // 媒体文件下载：GET 接口凭据走 URL 参数（后端支持），新窗口触发下载
        downloadMedia (row) {
            const fileName = `${row.msgtype}_${row.msgid || row.id}`
            const url = buildMediaDownloadUrl(row.sdkfileid, fileName, this.currentApp)
            window.open(url, '_blank')
        }
    }
}
</script>

<style lang="scss" scoped>
.qw-message-list__no-content {
    color: var(--qw-text-tertiary);
    font-size: 12px;
}
</style>
